import { round, sum } from 'es-toolkit';
import {
  nativeAppStateV1Schema,
  NativeState,
  NativeStateType,
} from '../models/native-state';
import { Episode } from '../models/podcasts';
import debugLog from '../utils/debug';
import { reportSentryError, reportSentryWarning } from '../utils/error-handling';
import { DeferredPromise, exposeOnWindow } from '../utils/helpers';
import { persistCurrentEpisodeProgress } from '../utils/persist-progress';
import { isNativeAppWithStateV1 } from '../utils/platform';
import {
  currentDuration,
  currentEpisode,
  currentPlaylist,
  currentSeek,
  playbackRate,
  sleepTimer,
  state,
} from './audio-signals';
import { consumeNativeUsageFromState } from './native-usage';
import { idToEpisode } from './podcasts';
import { currentUserId } from './user';
import { setEpisodeProgress, useUserDataStore } from './user-data';

const log = (...args: any[]) => {
  debugLog({ logName: 'native-state', logColor: 'gold' }, ...args);
};

//
// Parse native app state from last session and expose it
//

const nativeStateFromLastSession = parseNativeState('last-session');
export const getNativeStateFromLastSession = () => nativeStateFromLastSession;

let nativeState = parseNativeState('current');
export const getNativeState = () => nativeState;
let ignoreNativePlaylistUpdatesUntil = 0;

export function markUpcomingEpisodesJustSyncedToNative() {
  ignoreNativePlaylistUpdatesUntil = Date.now() + 1500;
}

log('Native state', { current: nativeState, lastSession: nativeStateFromLastSession });

//
// Handle native app state changes
//

const startSyncingPromise = new DeferredPromise();
if (typeof window !== 'undefined') {
  startSyncingPromise.promise.then(() => {
    const parseAndUpdate = () => {
      nativeState = parseNativeState('current');
      if (nativeState) handleUpdates(nativeState, 'current');
    };
    // Webview will fire these events when state changes while the app is visible
    addEventListener('UPDATE_NATIVE_APP_STATE_V1', parseAndUpdate);
    // When coming back from background, update state
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') parseAndUpdate();
    });
  });
}

export let nativeStateUpdateCount = 0;
export function handleUpdates(nativeState: NativeState, type: 'current') {
  nativeStateUpdateCount++;
  if ((nativeState.userId || null) !== currentUserId.value) {
    // The native app is initially not associated with any user, so this is expected
    // initially when the frontend is signed in.
    if (!nativeState.userId && nativeStateUpdateCount === 1) return;

    reportSentryWarning(new Error('Ignoring native state update for different user'), {
      nativeUserId: nativeState.userId,
      currentUserId: currentUserId.value,
      type,
      nativeState,
      nativeStateUpdateCount,
    });
    return;
  }

  syncEpisodesFromNativeState(nativeState);

  // Update seek to match native state
  currentSeek.value = getNativePosition(nativeState, type);

  // We only save currentSeek to user data (progress) when the state is playing/seeking.
  // If it's not the case, we still want to save the progress from the native state, such
  // as when episode is paused or finished and user brings app back to the foreground.
  if (!['playing', 'seeking'].includes(state.value)) {
    log(`Persisting native progress while ${state.value}`);
    persistCurrentEpisodeProgress(nativeState.updatedAt);
  }

  sleepTimer.value = nativeState.sleepTimer;
  state.value = nativeState.state;
  if (document.visibilityState === 'visible') {
    // Track ad-skips right away, but don't track minutes listened. We only do this right
    // before we sync usage data, every few minutes. We could do it more often but there's
    // no need to since it's just for crude analytics
    consumeNativeUsageFromState(nativeState, type, { shouldConsumeMinutes: false });
  }
}

// Sync initial state with native app state, or return false to indicate
// that we should use LocalStorage instead of native app state
export function initializeBasedOnNativeAppState(
  loadNewEpisode: (episode: Episode, seek: number) => void
): boolean {
  startSyncingPromise.resolve(undefined);

  if (!nativeState) return false;

  // Use native state from last session if this native session has just started
  // (i.e. user just opened the app). Otherwise, use current native state (e.g.
  // user just refreshed the page while app was open).
  let nativeStateToUse: NativeState;
  let nativeStateType: 'current' | 'last-session';

  // How long before navigation was current native state initialized? Should be
  // just a few milliseconds if app was just opened.
  const currentNativeStateInitializedAgo =
    performance.timeOrigin - nativeState.initializedAt;

  // Use current native state unless it was recently initialized˝
  const canUseCurrentNativeState =
    nativeState.source !== 'initial' &&
    Math.abs(currentNativeStateInitializedAgo) > 1000;

  if (canUseCurrentNativeState) {
    log('Using current native state');
    nativeStateToUse = nativeState;
    nativeStateType = 'current';
  } else if (nativeStateFromLastSession) {
    log('Using native state from last session');
    nativeStateToUse = nativeStateFromLastSession;
    nativeStateType = 'last-session';
  } else {
    return false;
  }

  if ((nativeStateToUse.userId || null) !== currentUserId.value) {
    reportSentryWarning(new Error('Ignoring native startup state for different user'), {
      nativeUserId: nativeStateToUse.userId,
      currentUserId: currentUserId.value,
      nativeStateType,
      nativeStateToUse,
    });
    return false;
  }

  // Get initial episode ID
  const episodeId = nativeStateToUse.episodes[0]?.id;

  // Native app is in an empty state and that is ok.
  if (!episodeId) return true;

  // Bail if initial episode is not found
  let episodeToUse: Episode | undefined =
    window.globalPodcastActions.idToEpisode(episodeId);
  if (!episodeToUse) {
    reportSentryWarning(new Error('Initial native episode not found'), {
      episodeId,
      nativeStateToUse,
    });
    return true;
  }

  // Set initial seek of this episode
  let seekToUse = chooseNativeOrUserDataPosition(
    episodeToUse,
    nativeStateToUse,
    nativeStateType
  );

  // User just finished playing this episode. Don't use it.
  if (seekToUse === 0) {
    log('Initial seek of episode is 0, ignoring it', { episodeId });
    return true;
  }

  // Set initial episode
  currentEpisode.value = episodeToUse;
  currentSeek.value = seekToUse;

  // Set initial playlist state
  currentPlaylist.value = nativeStateToUse.episodes
    .slice(1)
    .map((e) => [e.id, e.position]);

  // Native app is in an unloaded state and that is ok. No need to do anything
  if (nativeStateToUse.state === 'unloaded') return true;

  // Set initial sleep timer
  sleepTimer.value = nativeStateToUse.sleepTimer;

  // If app is already booted up, then we really don't need to do anything else
  // besides ensure our state matches the app
  if (nativeStateType === 'current') {
    state.value = nativeStateToUse.state;
    return true;
  }

  // App isn't booted up yet. Load initial episode...
  loadNewEpisode(episodeToUse, currentSeek.value);
  return true;
}

// Update the position of the current episode to match native state, assuming of course
// that native state has a newer value for position.
function chooseNativeOrUserDataPosition(
  episode: Episode,
  nativeStateToUse: NativeState,
  nativeStateType: NativeStateType
): number {
  const episodeId = episode.id;
  const userDataState = useUserDataStore.getState();
  const progressTimestamp: number | undefined =
    userDataState.episodesProgressSaveTimes[episodeId];
  const progress = userDataState.episodesProgress[episodeId];
  const userDataPosition = progress ? progress[0] : 0;
  const nativePosition = getNativePosition(nativeStateToUse, nativeStateType);

  if (nativePosition === userDataPosition) {
    log(`Native pos (${nativePosition}) matches user-data pos (${userDataPosition})`);
    return nativePosition;
  }

  const updatedAtDiffMs = nativeStateToUse.updatedAt - progressTimestamp;
  let choice: { reason: string; position: number };
  if (updatedAtDiffMs > 0) {
    // Native state is newer
    choice = { reason: 'native is newer', position: nativePosition };
  } else if (
    updatedAtDiffMs > -1000 &&
    nativePosition > userDataPosition &&
    nativePosition < userDataPosition + 1500 * playbackRate.value
  )
    // Native state was updated within a second of user-data and native position is newer
    // This is expected b/c user-data state updates are throttled
    choice = { reason: 'native is close', position: nativePosition };
  else {
    choice = { reason: 'user-data is newer', position: userDataPosition };
  }

  if (choice.position === nativePosition && choice.position !== userDataPosition) {
    // Set episode progress to update user-data position to match native one
    const duration =
      episode.duration || Math.max(nativePosition, progress ? sum(progress) : 0);
    setEpisodeProgress(episode, nativePosition, duration, nativeStateToUse.updatedAt);

    // Sanity check: make sure progress updated
    const after = useUserDataStore.getState().episodesProgress[episodeId];
    if (!after || round(after[0]) !== round(nativePosition)) {
      reportSentryWarning(
        new Error('Failed to set episode progress from native state'),
        {
          episodeId,
          duration,
          nativePosition,
          updatedAtDiffMs,
          before: progress,
          after,
        }
      );
    }
  }

  log(`Chose initial position: ${choice.reason}`, {
    nativePosition,
    userDataPosition,
    updatedAtDiffMs,
  });

  return choice.position;
}

//
// Utils
//

function syncEpisodesFromNativeState(nativeState: NativeState) {
  const nativeCurrentEpisodeId = nativeState.episodes[0]?.id;
  const localCurrentEpisodeId = currentEpisode.value?.id;

  if (nativeCurrentEpisodeId && nativeCurrentEpisodeId !== localCurrentEpisodeId) {
    const episode = idToEpisode(nativeCurrentEpisodeId);
    if (!episode) {
      reportSentryWarning(
        new Error('Unable to sync current episode from native state'),
        {
          nativeCurrentEpisodeId,
          nativeState,
        }
      );
    } else {
      currentEpisode.value = episode;
      currentDuration.value = episode.duration || NaN;
    }
  }

  const nativePlaylist = nativeState.episodes
    .slice(1)
    .map((e) => [e.id, e.position] as [typeof e.id, number]);

  // When the frontend just pushed a playlist update to Android, native can briefly echo back
  // the prior playlist before its own state settles. Ignore that short-lived stale overwrite,
  // but still accept native updates right away if the current episode itself changed.
  if (
    nativeCurrentEpisodeId === localCurrentEpisodeId &&
    Date.now() < ignoreNativePlaylistUpdatesUntil
  ) {
    return;
  }

  currentPlaylist.value = nativePlaylist;
}

function getNativePosition(
  nativeState: NativeState,
  session: 'current' | 'last-session'
): number {
  const nativePosition = nativeState.episodes[0]?.position || 0;

  // No need to account for drift if we're using native state saved last session
  if (session === 'last-session') return nativePosition;

  // Account for drift and playback rate if currently playing
  if (nativeState.state === 'playing') {
    const now = Date.now();
    const timeSinceUpdated = now - nativeState.updatedAt;
    let driftSeconds = timeSinceUpdated / 1000;

    // We never expect to see drift over a second or two
    if (driftSeconds > 3) {
      reportSentryWarning(new Error('Unexpectedly high seek drift'), {
        driftSeconds,
        timeSinceUpdated,
        now,
        nativeState,
      });
      driftSeconds = 3;
    }

    const adjustment = driftSeconds * playbackRate.value; // Apply playback rate
    return nativePosition + adjustment;
  }

  return nativePosition;
}

function parseNativeState(type: NativeStateType): NativeState | undefined {
  if (typeof window === 'undefined' || !isNativeAppWithStateV1) return;

  const rawState = getAndroidRawNativeStateV1(type);
  if (type === 'last-session' && rawState === undefined) return;

  try {
    return nativeAppStateV1Schema.parse(rawState);
  } catch (error) {
    reportSentryError(error as Error, { state: rawState, type });
    return;
  }
}
exposeOnWindow({ parseNativeState });

function getAndroidRawNativeStateV1(type: NativeStateType): unknown {
  let raw;
  try {
    // @ts-expect-error
    raw = window.Android.getState(type);
    if (type === 'last-session' && raw === undefined) return; // Bail early if there's no native state

    return JSON.parse(raw);
  } catch (error) {
    reportSentryError(error as Error, {
      type,
      context: 'Failed to parse Android native app state',
      raw,
    });
  }
}
